/**
 * KisiKullanicilarSekmesi.jsx — denetmen kişi sayfasının (PersonelDetayPage) "Kullanıcılar" sekmesi.
 * Backlog #380 (backend #377): kişiye bağlı FDS kullanıcıları, "Kullanıcı Bağla" ve "Kullanıcı Oluştur".
 *
 * Bir kişiye birden çok kullanıcı bağlanabilir, bir kullanıcı en çok bir kişiye (bkz. kisiKullaniciService.js). Bağlantı
 * yalnızca "bu kullanıcı kim" kaydıdır: kullanıcı kişinin kendi girişi olmaz, hiçbir yetki vermez.
 *
 * Yazmalar `kisi.kullanici.manage` ister; okuma yanıtındaki `duzenleyebilir` bunu söyler, o yoksa (ya da `salt` ise,
 * yani birleştirilmiş kart) düğmeler hiç çizilmez.
 *
 * "Kullanıcı Bağla": seçici sunucudaki ADAY uçundan beslenir (boş arama = kişiyle ad soyad / telefon / e-posta eşleşen öneriler;
 * 2+ karakter = arama, 300 ms bekletmeli). Bir adaya tıklamak bağlar. İki 409 beklenir:
 *   · UYARI_ONAYI_GEREKLI — ad soyad farklı/boş → uyarılar gösterilir, "Yine de bağla" `uyarilarOnaylandi: true` ile yeniden yollar
 *   · KULLANICI_BASKA_KISIYE_BAGLI — kullanıcı başka kişide → o kişinin sayfasına yeni sekmede bağlantı (önce orada kaldırılır)
 * Bağlantıyı kaldırmak kullanıcıyı silmez; satır "Geçmiş"te kalır.
 *
 * "Kullanıcı Oluştur" (#545, 2026-10-26): kişinin ad, soyad, telefon ve e-postasıyla ÖNCEDEN DOLDURULMUŞ kullanıcı formunu (UserFormModal,
 * mode="create") açar, POST /users ile kullanıcıyı yaratır ve hemen bu kişiye bağlar. Düğme `kisi.kullanici.manage` (bağlamak için) VE
 * MANAGE_USER_ROLES (yaratmak için) ister; ikincisi yoksa hiç çizilmez. Bağlama, yaratılan kullanıcı tam bu kişi için olduğundan uyarı
 * onayıyla (`uyarilarOnaylandi: true`) gider — form ad / soyadı değiştirilmiş olsa bile. Yaratma başarılı, bağlama başarısızsa pencere
 * bunu söyler (kullanıcı vardır; "Kullanıcı Bağla" ile bağlanır). WAS (#380, 2026-10-13): yalnızca "kullanıcılar eski sistemde oluşturulur"
 * bilgi penceresi (sunucuda POST /users reddediliyordu, #306 1b; #524 ile açıldı).
 *
 * @param {number}   personId
 * @param {boolean}  salt         birleştirilmiş kart: yalnızca okunur
 * @param {object}   kisi         { ad, soyad, telefon, eposta } — "Kullanıcı Oluştur" formunu önceden doldurur
 * @param {number}   reloadToken  değişince yeniden okunur
 * @param {Function} onChanged    `(mesaj)` — yazma başarılı
 * @param {Function} onError      `(mesaj)` — kaldırma başarısız (bağla penceresi hatayı kendi içinde gösterir)
 */
import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Alert, Badge, Button, Card, CardBody, CardHeader, Input, ListGroup, Modal, ModalBody, ModalFooter, ModalHeader, Spinner, Table,
} from "reactstrap";
import { Users, Link2, UserPlus, Unlink, Search, ChevronDown, ChevronRight, AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  getKisiKullanicilari, baglaKisiKullanici, kaldirKisiKullanici, araKullanici,
} from "../../../services/kisiKullaniciService";
import { kisiSayfasiUrl } from "../kisiBaglantilari";
import { PERMISSION_KEYS, SAYFA_IZNI, useYetki } from "../../../services/rbacService";
import UserFormModal from "../../../pages/Management/Users/UserFormModal";
import i18n, { currentLocaleTag } from "../../../i18n";

function tarihSaat(v) {
  if (!v) return null;
  const d = new Date(String(v).replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString(currentLocaleTag(), { dateStyle: "short", timeStyle: "short" });
}

const adGoster = (u) => u.adSoyad?.trim() || u.kullaniciAdi || i18n.t("components:kisiKullanicilarSekmesi.kullaniciNo", { id: u.userId });

/** Bu temada `success` gri, `secondary` yeşil basılır: olumlu rozet `secondary`. */
function AktifRozeti({ u }) {
  const { t } = useTranslation("components");
  if (u.kullaniciVar === false) return <Badge color="danger" className="fw-normal">{t("kisiKullanicilarSekmesi.silinmis")}</Badge>;
  return u.aktif
    ? <Badge color="secondary" className="fw-normal">{t("kisiKullanicilarSekmesi.aktif")}</Badge>
    : <Badge color="light" className="text-body border fw-normal">{t("kisiKullanicilarSekmesi.pasif")}</Badge>;
}

function FirmaHucresi({ u }) {
  const { t } = useTranslation("components");
  if (!u.firmaAdi) return <span className="text-muted">—</span>;
  return (
    <span className="d-inline-flex align-items-center gap-1 flex-wrap">
      <span>{u.firmaAdi}</span>
      {u.firmaYabanci === true && <Badge color="info-subtle" className="text-info fw-normal">{t("kisiKullanicilarSekmesi.yabanci")}</Badge>}
      {u.firmaYabanci === false && <Badge color="light" className="text-body border fw-normal">{t("kisiKullanicilarSekmesi.yerli")}</Badge>}
      {u.firmaSayisi > 1 && (
        <span className="text-muted small" title={t("kisiKullanicilarSekmesi.firmadaKayitli", { count: u.firmaSayisi })}>+{u.firmaSayisi - 1}</span>
      )}
    </span>
  );
}

function bagliyanSatiri(u) {
  return [u.baglayanAdi, tarihSaat(u.baglamaTarihi)].filter(Boolean).join(" · ") || "—";
}

function aramaHatasi(e) {
  if (e?.status === 403) return i18n.t("components:kisiKullanicilarSekmesi.aramaYalnizcaPersonel");
  return e?.message || i18n.t("components:kisiKullanicilarSekmesi.kullanicilarAranamadi");
}

/** "Kullanıcı Bağla" penceresi: aday seçici + iki 409'un işlenişi. Açıldığında bağlanır (kapanınca durumu sıfırlanır). */
function KullaniciBaglaModal({ personId, onClose, onBaglandi }) {
  const { t } = useTranslation("components");
  const location = useLocation();
  const [q, setQ] = useState("");
  const [sonuc, setSonuc] = useState(null); // { anahtar, veri, hata }
  const [isleniyor, setIsleniyor] = useState(null); // bağlanan userId
  const [hata, setHata] = useState(null);
  const [uyari, setUyari] = useState(null); // { aday, uyarilar[] }
  const [cakisma, setCakisma] = useState(null); // { aday, bagliPersonId, bagliAdSoyad }

  const metin = q.trim();
  const aramaGecerli = metin.length !== 1; // sunucu tek karakterli aramayı 400 ile reddeder
  const yukleniyor = aramaGecerli && sonuc?.anahtar !== metin;

  // Debounce: yazarken 300 ms bekler, boş arama (öneriler) hemen gider. setState zamanlayıcı geri çağrısının içinde.
  useEffect(() => {
    if (metin.length === 1) return undefined;
    let iptal = false;
    const zamanlayici = setTimeout(async () => {
      try {
        const d = await araKullanici(personId, metin);
        if (!iptal) setSonuc({ anahtar: metin, veri: d, hata: null });
      } catch (e) {
        if (!iptal) setSonuc({ anahtar: metin, veri: null, hata: aramaHatasi(e) });
      }
    }, metin ? 300 : 0);
    return () => { iptal = true; clearTimeout(zamanlayici); };
  }, [personId, metin]);

  const adaylar = sonuc?.veri?.adaylar || [];
  const geri = `${location.pathname}${location.search}`;

  async function bagla(aday, onayli = false) {
    setIsleniyor(aday.userId);
    setHata(null);
    try {
      const d = await baglaKisiKullanici(personId, aday.userId, onayli);
      onBaglandi(d, d?.degisti === false
        ? t("kisiKullanicilarSekmesi.zatenBagli", { ad: adGoster(aday) })
        : t("kisiKullanicilarSekmesi.kisiyeBaglandi", { ad: adGoster(aday) }));
    } catch (e) {
      if (e.status === 409 && e.kod === "UYARI_ONAYI_GEREKLI") {
        setCakisma(null);
        setUyari({ aday, uyarilar: Array.isArray(e.data?.uyarilar) ? e.data.uyarilar : [] });
      } else if (e.status === 409 && e.kod === "KULLANICI_BASKA_KISIYE_BAGLI") {
        setUyari(null);
        setCakisma({ aday, bagliPersonId: e.data?.bagliPersonId, bagliAdSoyad: e.data?.bagliAdSoyad });
      } else {
        setUyari(null);
        setCakisma(null);
        setHata(e.message || t("kisiKullanicilarSekmesi.kullaniciBaglanamadi"));
      }
    } finally {
      setIsleniyor(null);
    }
  }

  const kapat = () => isleniyor == null && onClose();
  const eslesmeEtiketi = { AD_SOYAD: t("kisiKullanicilarSekmesi.eslesmeAdSoyad"), TELEFON: t("kisiKullanicilarSekmesi.eslesmeTelefon"), EPOSTA: t("kisiKullanicilarSekmesi.eslesmeEposta") };

  return (
    <Modal isOpen toggle={kapat} size="lg">
      <ModalHeader toggle={kapat}>{t("kisiKullanicilarSekmesi.kullaniciBagla")}</ModalHeader>
      <ModalBody>
        {hata && <Alert fade={false} color="danger" className="py-2 small" toggle={() => setHata(null)}>{hata}</Alert>}

        {cakisma && (
          <Alert fade={false} color="danger" className="small mb-0">
            <strong>{adGoster(cakisma.aday)}</strong> {t("kisiKullanicilarSekmesi.baskaKisiyeBagli")}{" "}
            {cakisma.bagliPersonId != null ? (
              <a
                href={kisiSayfasiUrl({
                  personId: cakisma.bagliPersonId, tab: "kullanicilar", geri, geriEtiket: t("kisiKullanicilarSekmesi.kullanicilaraDon"),
                })}
                target="_blank" rel="noreferrer" className="fw-semibold"
              >
                {cakisma.bagliAdSoyad || t("kisiKullanicilarSekmesi.kisiNo", { id: cakisma.bagliPersonId })}
              </a>
            ) : (cakisma.bagliAdSoyad || t("kisiKullanicilarSekmesi.baskaKisi"))}
            . {t("kisiKullanicilarSekmesi.onceKaldirilmali")}
          </Alert>
        )}

        {uyari && (
          <Alert fade={false} color="warning" className="small mb-0">
            <div className="d-flex align-items-center gap-2 fw-semibold mb-1">
              <AlertTriangle size={16} />{t("kisiKullanicilarSekmesi.buKisiyeAitGorunmuyor", { ad: adGoster(uyari.aday) })}
            </div>
            <ul className="mb-0 ps-3">
              {uyari.uyarilar.map((u, i) => <li key={u.kod ? `${u.kod}-${i}` : i}>{u.mesaj}</li>)}
            </ul>
          </Alert>
        )}

        {!cakisma && !uyari && (
          <>
            <div className="position-relative mb-2">
              <Search size={14} className="position-absolute text-muted" style={{ left: 10, top: 11 }} />
              <Input
                autoFocus value={q} onChange={(e) => setQ(e.target.value)} style={{ paddingLeft: 30 }}
                placeholder={t("kisiKullanicilarSekmesi.aramaYeri")}
              />
              {yukleniyor && <Spinner size="sm" color="primary" className="position-absolute" style={{ right: 10, top: 10 }} />}
            </div>

            {sonuc?.hata && <div className="text-danger small">{sonuc.hata}</div>}
            {!sonuc && <div className="py-2"><Spinner size="sm" color="primary" /></div>}

            {sonuc?.veri && (
              <>
                {sonuc.veri.oneri && adaylar.length > 0 && (
                  <div className="small text-muted mb-1">{t("kisiKullanicilarSekmesi.buKisiyleEslesenler")}</div>
                )}
                {adaylar.length === 0 && !yukleniyor && <div className="text-muted small py-2">{t("kisiKullanicilarSekmesi.eslesenKullaniciYok")}</div>}
                {adaylar.length > 0 && (
                  <ListGroup tag="div" className="border rounded" style={{ maxHeight: 360, overflowY: "auto", opacity: yukleniyor ? 0.5 : 1 }}>
                    {adaylar.map((a) => (
                      <button
                        key={a.userId} type="button"
                        className="list-group-item list-group-item-action text-start"
                        disabled={a.buKisiyeBagli || isleniyor != null}
                        onClick={() => bagla(a)}
                      >
                        <div className="d-flex align-items-start gap-2">
                          <div className="flex-grow-1" style={{ minWidth: 0 }}>
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                              <span className="fw-semibold">{adGoster(a)}</span>
                              <span className="text-muted small">{a.kullaniciAdi}</span>
                              {!a.aktif && <Badge color="light" className="text-body border fw-normal">{t("kisiKullanicilarSekmesi.pasif")}</Badge>}
                              {(a.eslesme || []).map((e) => (
                                <Badge key={e} color="secondary" className="fw-normal">{eslesmeEtiketi[e] || e}</Badge>
                              ))}
                            </div>
                            <div className="small text-muted d-flex align-items-center gap-2 flex-wrap">
                              {a.grupAdi && <span>{a.grupAdi}</span>}
                              {a.firmaAdi && <FirmaHucresi u={a} />}
                            </div>
                          </div>
                          <div className="flex-shrink-0">
                            {isleniyor === a.userId && <Spinner size="sm" color="primary" />}
                            {a.buKisiyeBagli && <Badge color="secondary" className="fw-normal">{t("kisiKullanicilarSekmesi.buKisiyeBagliRozet")}</Badge>}
                            {a.baskaKisiyeBagli && (
                              <Badge color="light" className="text-body border fw-normal" title={a.bagliAdSoyad || undefined}>
                                {t("kisiKullanicilarSekmesi.baskaKisiyeBagliRozet")}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </ListGroup>
                )}
                {sonuc.veri.fazlaVar && <div className="small text-muted mt-1">{t("kisiKullanicilarSekmesi.ilk50")}</div>}
              </>
            )}
            {metin.length === 1 && <div className="small text-muted mt-1">{t("kisiKullanicilarSekmesi.enAz2Karakter")}</div>}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        {(uyari || cakisma) && (
          <Button color="light" onClick={() => { setUyari(null); setCakisma(null); }} disabled={isleniyor != null}>{t("common:geri")}</Button>
        )}
        {uyari && (
          <Button color="warning" onClick={() => bagla(uyari.aday, true)} disabled={isleniyor != null}>
            {isleniyor != null ? <Spinner size="sm" /> : t("kisiKullanicilarSekmesi.yineDeBagla")}
          </Button>
        )}
        <Button color="light" onClick={kapat} disabled={isleniyor != null}>{t("common:kapat")}</Button>
      </ModalFooter>
    </Modal>
  );
}

export default function KisiKullanicilarSekmesi({ personId, salt = false, kisi = null, reloadToken = 0, onChanged, onError }) {
  const { t } = useTranslation("components");
  const location = useLocation();
  // #379: kullanıcının adı Kullanıcı Kartı'na gider (page.kullanici); izni olmayan için düz yazı.
  const kartAcabilir = useYetki(SAYFA_IZNI.KULLANICI) === true;
  // #545: kullanıcıyı YARATMAK MANAGE_USER_ROLES ister (bağlamak ayrıca kisi.kullanici.manage); yoksa "Kullanıcı Oluştur" hiç çizilmez.
  const olusturabilir = useYetki(PERMISSION_KEYS.MANAGE_USER_ROLES) === true;
  const geri = `${location.pathname}${location.search}`;
  const [veri, setVeri] = useState(null);
  const [okunuyor, setOkunuyor] = useState(false);
  const [okumaHatasi, setOkumaHatasi] = useState(null);
  const [baglaAcik, setBaglaAcik] = useState(false);
  const [olusturAcik, setOlusturAcik] = useState(false);
  const [kaldirHedef, setKaldirHedef] = useState(null);
  const [kaldiriliyor, setKaldiriliyor] = useState(false);
  const [gecmisAcik, setGecmisAcik] = useState(false);

  // Fetch + setState async fonksiyonun içinde (react-hooks/set-state-in-effect).
  useEffect(() => {
    if (personId == null) return undefined;
    let iptal = false;
    async function oku() {
      setOkunuyor(true);
      setOkumaHatasi(null);
      try {
        const d = await getKisiKullanicilari(personId);
        if (!iptal) setVeri(d);
      } catch (e) {
        if (!iptal) setOkumaHatasi(e.message || i18n.t("components:kisiKullanicilarSekmesi.kullanicilarOkunamadi"));
      } finally {
        if (!iptal) setOkunuyor(false);
      }
    }
    oku();
    return () => { iptal = true; };
  }, [personId, reloadToken]);

  const kullanicilar = veri?.kullanicilar || [];
  const gecmis = veri?.gecmis || [];
  const duzenleyebilir = !!veri?.duzenleyebilir && !salt;

  function baglandi(d, mesaj) {
    if (d) setVeri(d);
    setBaglaAcik(false);
    onChanged?.(mesaj);
  }

  async function kaldir() {
    if (!kaldirHedef) return;
    const hedef = kaldirHedef;
    setKaldiriliyor(true);
    try {
      const d = await kaldirKisiKullanici(personId, hedef.userId);
      if (d) setVeri(d);
      setKaldirHedef(null);
      onChanged?.(t("kisiKullanicilarSekmesi.baglantiKaldirildi", { ad: adGoster(hedef) }));
    } catch (e) {
      setKaldirHedef(null);
      onError?.(e.message || t("kisiKullanicilarSekmesi.baglantiKaldirilamadi"));
    } finally {
      setKaldiriliyor(false);
    }
  }

  return (
    <Card className="mb-3">
      <CardHeader className="bg-transparent d-flex align-items-center gap-2 flex-wrap">
        <Users size={16} className="text-muted" />
        <span className="fw-semibold">{t("kisiKullanicilarSekmesi.kullanicilar")}</span>
        {veri && <Badge color="light" className="text-body border fw-normal">{kullanicilar.length}</Badge>}
        {okunuyor && <Spinner size="sm" color="primary" />}
        {duzenleyebilir && (
          <div className="ms-auto d-flex align-items-center gap-2">
            <Button color="green" size="sm" onClick={() => setBaglaAcik(true)}>
              <Link2 size={13} className="me-1" />{t("kisiKullanicilarSekmesi.kullaniciBagla")}
            </Button>
            {olusturabilir && (
              <Button color="primary" outline size="sm" onClick={() => setOlusturAcik(true)}>
                <UserPlus size={13} className="me-1" />{t("kisiKullanicilarSekmesi.kullaniciOlustur")}
              </Button>
            )}
          </div>
        )}
      </CardHeader>
      <CardBody>
        {okumaHatasi && <div className="text-danger small">{okumaHatasi}</div>}
        {!veri && !okumaHatasi && <div className="py-2"><Spinner size="sm" color="primary" /></div>}

        {veri && kullanicilar.length === 0 && <div className="text-muted small">{t("kisiKullanicilarSekmesi.bagliKullaniciYok")}</div>}

        {kullanicilar.length > 0 && (
          <div className="table-responsive">
            <Table className="table-sm align-middle mb-0">
              <thead className="table-light text-muted small">
                <tr>
                  <th>{t("kisiKullanicilarSekmesi.kullanici")}</th>
                  <th>{t("kisiKullanicilarSekmesi.grup")}</th>
                  <th>{t("kisiKullanicilarSekmesi.firma")}</th>
                  <th>{t("kisiKullanicilarSekmesi.sonGiris")}</th>
                  <th>{t("kisiKullanicilarSekmesi.baglayan")}</th>
                  {duzenleyebilir && <th />}
                </tr>
              </thead>
              <tbody>
                {kullanicilar.map((u) => (
                  <tr key={u.userId}>
                    <td>
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        {kartAcabilir && u.kullaniciVar !== false ? (
                          <Link
                            to={`/Users/${u.userId}?geri=${encodeURIComponent(geri)}&geriEtiket=${encodeURIComponent(t("kisiKullanicilarSekmesi.kisiSayfasinaDon"))}`}
                            className="fw-semibold text-body" title={t("kisiKullanicilarSekmesi.kullaniciKarti")}
                          >
                            {adGoster(u)}
                          </Link>
                        ) : <span className="fw-semibold">{adGoster(u)}</span>}
                        <AktifRozeti u={u} />
                      </div>
                      <div className="small text-muted" title={t("kisiKullanicilarSekmesi.kullaniciNo", { id: u.userId })}>{u.kullaniciAdi}</div>
                    </td>
                    <td className="small">{u.grupAdi || <span className="text-muted">—</span>}</td>
                    <td className="small"><FirmaHucresi u={u} /></td>
                    <td className="small text-nowrap">{tarihSaat(u.sonGiris) || <span className="text-muted">—</span>}</td>
                    <td className="small">{bagliyanSatiri(u)}</td>
                    {duzenleyebilir && (
                      <td className="text-end">
                        <Button color="danger" outline size="sm" onClick={() => setKaldirHedef(u)}>
                          <Unlink size={13} className="me-1" />{t("kisiKullanicilarSekmesi.baglantiyiKaldir")}
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        {gecmis.length > 0 && (
          <div className="mt-3">
            <Button color="link" size="sm" className="p-0 text-muted text-decoration-none" onClick={() => setGecmisAcik((a) => !a)}>
              {gecmisAcik ? <ChevronDown size={14} className="me-1" /> : <ChevronRight size={14} className="me-1" />}
              {t("kisiKullanicilarSekmesi.gecmis", { count: gecmis.length })}
            </Button>
            {gecmisAcik && (
              <div className="table-responsive mt-2">
                <Table className="table-sm align-middle mb-0 text-muted">
                  <thead className="small">
                    <tr>
                      <th>{t("kisiKullanicilarSekmesi.kullanici")}</th><th>{t("kisiKullanicilarSekmesi.baglayan")}</th><th>{t("kisiKullanicilarSekmesi.kaldiran")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gecmis.map((u) => (
                      <tr key={u.baglantiId ?? `${u.userId}-${u.baglamaTarihi}`}>
                        <td>
                          <span className="text-body">{adGoster(u)}</span>
                          {u.kullaniciAdi && <span className="small ms-2">{u.kullaniciAdi}</span>}
                        </td>
                        <td className="small">{bagliyanSatiri(u)}</td>
                        <td className="small">
                          {[u.kaldiranAdi, tarihSaat(u.kaldirmaTarihi)].filter(Boolean).join(" · ") || "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </div>
        )}
      </CardBody>

      {baglaAcik && (
        <KullaniciBaglaModal personId={personId} onClose={() => setBaglaAcik(false)} onBaglandi={baglandi} />
      )}

      {olusturAcik && (
        <UserFormModal
          mode="create" varsayilan={kisi}
          sonrasi={(userId) => baglaKisiKullanici(personId, userId, true)}
          onClose={(sonuc) => {
            setOlusturAcik(false);
            if (!sonuc) return;
            // Bağlama başarısızsa pencere zaten söyledi; yine de kullanıcı oluştu, sekme yeniden okunur.
            onChanged?.(sonuc.baglanamadi ? undefined : t("kisiKullanicilarSekmesi.olusturulduVeBaglandi"));
          }}
        />
      )}

      <Modal isOpen={!!kaldirHedef} toggle={() => !kaldiriliyor && setKaldirHedef(null)}>
        <ModalHeader toggle={() => !kaldiriliyor && setKaldirHedef(null)}>{t("kisiKullanicilarSekmesi.baglantiKaldirilsinMi")}</ModalHeader>
        <ModalBody className="small">
          {kaldirHedef && (
            <p className="mb-0">
              {t("kisiKullanicilarSekmesi.kaldirmaAciklamasi", {
                ad: adGoster(kaldirHedef), kullaniciAdi: kaldirHedef.kullaniciAdi ? ` (${kaldirHedef.kullaniciAdi})` : "",
              })}
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setKaldirHedef(null)} disabled={kaldiriliyor}>{t("kisiKullanicilarSekmesi.vazgec")}</Button>
          <Button color="danger" onClick={kaldir} disabled={kaldiriliyor}>
            {kaldiriliyor ? <Spinner size="sm" /> : t("kisiKullanicilarSekmesi.baglantiyiKaldir")}
          </Button>
        </ModalFooter>
      </Modal>
    </Card>
  );
}
